import React, { useState, useEffect } from 'react';

const BreederRequestModal = ({ 
  show, 
  onClose, 
  handler, 
  breeder, 
  onSubmit, 
  maxBirds = 5,
  approvedBirdsWithHandler = 0,
  remainingBirdsOverall = null
}) => {
  const [numberOfBirds, setNumberOfBirds] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [requestNote, setRequestNote] = useState('');
  const [birdDetails, setBirdDetails] = useState([
    { org: '', serialNumber: '', clubCode: '', year: '', color: '', sex: '' }
  ]);

  // Update birdDetails array when numberOfBirds changes
  useEffect(() => {
    const newBirdDetails = [...Array(numberOfBirds)].map((_, idx) => 
      birdDetails[idx] || { org: '', serialNumber: '', clubCode: '', year: '', color: '', sex: '' }
    );
    setBirdDetails(newBirdDetails);
  }, [numberOfBirds]);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Transform bird details to include band number
      const birdsWithBandNumbers = birdDetails.map(bird => ({
        bandNumber: getBandNumber(bird),
        org: bird.org,
        serialNumber: bird.serialNumber,
        clubCode: bird.clubCode,
        year: bird.year,
        color: bird.color || '[To be added]',
        sex: bird.sex || '[To be added]',
        status: 'placeholder'
      }));

      await onSubmit({
        handlerId: handler.id,
        breederId: breeder.id,
        numberOfBirds: numberOfBirds,
        birdDetails: birdsWithBandNumbers,
        requestNote: requestNote,
        status: 'pending'
      });
      
      // Reset form for next use
      setNumberOfBirds(1);
      setRequestNote('');
      setBirdDetails([{ org: '', serialNumber: '', clubCode: '', year: '', color: '', sex: '' }]);
      // Close modal after parent completes
      onClose();
    } catch (error) {
      console.error('Failed to submit request:', error);
      // Close modal even on error
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleIncrement = () => {
    if (numberOfBirds < maxBirds) {
      setNumberOfBirds(numberOfBirds + 1);
    }
  };

  const handleDecrement = () => {
    if (numberOfBirds > 1) {
      setNumberOfBirds(numberOfBirds - 1);
    }
  };

  const handleBirdDetailChange = (index, field, value) => {
    const newBirdDetails = [...birdDetails];
    
    // Validate serial number (max 15 digits)
    if (field === 'serialNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 15) {
        newBirdDetails[index][field] = numericValue;
      }
    }
    // Validate year (max 2 digits)
    else if (field === 'year') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 2) {
        newBirdDetails[index][field] = numericValue;
      }
    }
    // Other fields - no validation
    else {
      newBirdDetails[index][field] = value;
    }
    
    setBirdDetails(newBirdDetails);
  };

  const getBandNumber = (bird) => {
    if (!bird.org && !bird.serialNumber && !bird.clubCode && !bird.year) {
      return '[To be added]';
    }
    return `${bird.org || '?'}-${bird.serialNumber || '?'}-${bird.clubCode || '?'}-${bird.year || '?'}`;
  };

  return (
    <div 
      className="modal fade show" 
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ borderBottom: '3px solid #0b3d91' }}>
            <h5 className="modal-title" style={{ color: '#0b3d91' }}>
              <i className="bi bi-send me-2"></i>
              Request Handler Assignment
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              disabled={submitting}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-4">
                <h6 className="fw-bold mb-2">Handler Information</h6>
                <div className="p-3 bg-light rounded">
                  <div><strong>{handler.fullName}</strong></div>
                  <div className="text-muted small">{handler.loftName}</div>
                  <div className="text-muted small mt-1">
                    <div>Section: {handler.handlerInfo?.section || 'N/A'}</div>
                    <div>Distance: {handler.handlerInfo?.distanceFromRaceStation ? `${handler.handlerInfo.distanceFromRaceStation} miles` : 'N/A'}</div>
                  </div>
                </div>
              </div>

              {handler.handlerInfo?.requireBreederCall && (
                <div className="alert alert-warning mb-4">
                  <i className="bi bi-telephone-fill me-2"></i>
                  <strong>Call Required First:</strong> This handler requires you to call them at{' '}
                  <strong>{handler.mobile}</strong> to reserve a slot before submitting this request.
                  Please call them first to confirm availability.
                </div>
              )}

              <div className="alert alert-info mb-4">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Important:</strong> You're reserving space for your birds. 
                You'll need to update with actual bird details (band numbers, colors, etc.) 
                before shipping. 
                {maxBirds >= 999 ? (
                  'Admin rotation has no bird limit.'
                ) : (
                  <>Maximum {maxBirds} {maxBirds === 1 ? 'bird' : 'birds'} available for this request.</>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="numberOfBirds" className="form-label fw-bold">
                  Number of Birds <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-lg"
                    onClick={handleDecrement}
                    disabled={numberOfBirds <= 1 || submitting}
                    style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}
                  >
                    −
                  </button>
                  <div 
                    className="text-center fw-bold"
                    style={{ fontSize: '2.5rem', minWidth: '80px' }}
                  >
                    {numberOfBirds}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-lg"
                    onClick={handleIncrement}
                    disabled={numberOfBirds >= maxBirds || submitting}
                    style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}
                  >
                    +
                  </button>
                </div>
                <small className="text-muted">
                  {maxBirds >= 999 ? (
                    'No limit for admin rotation'
                  ) : (
                    <div>
                      <div className="mb-1">
                        You can request up to <strong>{maxBirds}</strong> {maxBirds === 1 ? 'bird' : 'birds'}
                      </div>
                      {approvedBirdsWithHandler > 0 && (
                        <div className="text-muted small">
                          • {approvedBirdsWithHandler} {approvedBirdsWithHandler === 1 ? 'bird' : 'birds'} already with this handler (5 max per handler)
                        </div>
                      )}
                    </div>
                  )}
                </small>
              </div>

              <div className="mb-3">
                <label htmlFor="requestNotes" className="form-label fw-bold">
                  Additional Notes <span className="text-muted">(Optional)</span>
                </label>
                <textarea
                  id="requestNotes"
                  className="form-control"
                  rows="3"
                  placeholder="Any special instructions or notes for the handler..."
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                  disabled={submitting}
                  maxLength={500}
                />
                <small className="text-muted">
                  {requestNote.length}/500 characters
                </small>
              </div>

              {/* Bird Information Section - Hidden for now */}
              {false && (
                <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <h6 className="fw-bold mb-3">Bird Information</h6>
                  <div className="text-muted small mb-3">
                    Enter bird details below. You can leave fields empty and update them later.
                  </div>
                  
                  {birdDetails.map((bird, idx) => (
                    <div key={idx} className="mb-4 pb-3 border-bottom">
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-primary me-2" style={{ fontSize: '0.9rem' }}>Bird {idx + 1}</span>
                        <small className="text-muted">
                          Band Number: <strong>{getBandNumber(bird)}</strong>
                        </small>
                      </div>
                      
                      <div className="row g-2">
                        {/* Band Number Components */}
                        <div className="col-md-3">
                          <label className="form-label small mb-1">Org Code</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="IF, AU, etc."
                            value={bird.org}
                            onChange={(e) => handleBirdDetailChange(idx, 'org', e.target.value.toUpperCase())}
                            disabled={submitting}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small mb-1">Serial # (max 15)</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="1234"
                            value={bird.serialNumber}
                            onChange={(e) => handleBirdDetailChange(idx, 'serialNumber', e.target.value)}
                            disabled={submitting}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small mb-1">Club Code</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="SCHOLAR"
                            value={bird.clubCode}
                            onChange={(e) => handleBirdDetailChange(idx, 'clubCode', e.target.value.toUpperCase())}
                            disabled={submitting}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small mb-1">Year (2 digits)</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="06"
                            value={bird.year}
                            onChange={(e) => handleBirdDetailChange(idx, 'year', e.target.value)}
                            disabled={submitting}
                          />
                        </div>
                        
                        {/* Color and Sex */}
                        <div className="col-md-6">
                          <label className="form-label small mb-1">Color</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Red, Blue, Check, etc."
                            value={bird.color}
                            onChange={(e) => handleBirdDetailChange(idx, 'color', e.target.value)}
                            disabled={submitting}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small mb-1">Sex</label>
                          <select
                            className="form-select form-select-sm"
                            value={bird.sex}
                            onChange={(e) => handleBirdDetailChange(idx, 'sex', e.target.value)}
                            disabled={submitting}
                          >
                            <option value="">Select...</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Unknown">Unknown</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Sending Request...' : 'Send Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BreederRequestModal;
